// src/pages/Profile/ProfileKankam.tsx

import { FC } from 'react';
import styles from '../../css/ProfilePage.module.css';
import { motion, Variants } from 'framer-motion';
import { ArrowLeft, Linkedin, Mail, ShieldCheck, Cloud, Server } from 'lucide-react';
import { Link } from 'react-router-dom';

import KankamImg from '../../assets/images/KankamProfile.jpeg';

const ProfileKankam: FC = () => {
    
    const fadeInUp: Variants = {
        hidden: { opacity: 0, y: 20 },
        visible: { 
            opacity: 1, 
            y: 0, 
            transition: { duration: 0.6, ease: "easeOut" } 
        }
    };

    const staggerContainer: Variants = {
        visible: {
            transition: { staggerChildren: 0.1 }
        }
    };

    return (
        <div className={styles.profilePage}>
            
            <section className={styles.heroSection}>
                <div className={styles.gridBackground}></div>
                <div className={styles.container}>
                    
                    {/* Back Navigation */}
                    <div className={styles.backLinkWrapper}>
                        <Link to="/about" className={styles.backLink}>
                            <ArrowLeft size={16} /> Back to Team
                        </Link>
                    </div>

                    <div className={styles.profileHeader}>
                        
                        {/* Left: Image & Socials */}
                        <motion.div 
                            className={styles.imageColumn}
                            initial={{ opacity: 0, scale: 0.95 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.5 }}
                        >
                            <div className={styles.profileImageWrapper}>
                                <img src={KankamImg} alt="Kankam Abrampa" className={styles.profileImage} />
                            </div>
                            <div className={styles.socialRow}>
                                <a 
                                    href="https://www.linkedin.com/in/kankamyeboah/" 
                                    target="_blank" 
                                    rel="noreferrer" 
                                    className={styles.socialBtn}
                                    aria-label="LinkedIn Profile"
                                >
                                    <Linkedin size={20} />
                                </a>
                                {/* Update email if he has a company email, otherwise generic or hidden */}
                                <a 
                                    href="mailto:kankamyeboah@@ota-erp.com" 
                                    className={styles.socialBtn}
                                    aria-label="Contact"
                                >
                                    <Mail size={20} />
                                </a>
                            </div>
                        </motion.div>

                        {/* Right: Biography */}
                        <motion.div 
                            className={styles.infoColumn}
                            initial="hidden"
                            animate="visible"
                            variants={staggerContainer}
                        >
                            <motion.h1 className={styles.name} variants={fadeInUp}>
                                Kankam Abrampa
                            </motion.h1>
                            <motion.div className={styles.title} variants={fadeInUp}>
                                Lead DevOps Engineer
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Role at OTA TECH</h2>
                                <p className={styles.bioText}>
                                    Kankam serves as the Lead DevOps Engineer, responsible for the architectural integrity and scalability of OTA TECH’s cloud infrastructure.
                                </p>
                                <p className={styles.bioText}>
                                    He oversees the deployment of <strong>Kubernetes</strong> clusters, manages <strong>AWS</strong> cloud environments, and architects robust <strong>CI/CD pipelines</strong> that allow our engineering team to ship code rapidly without compromising stability. 
                                </p>
                                <p className={styles.bioText}>
                                    Additionally, he conducts high-level code reviews for security vulnerabilities and quality assurance, ensuring the platform meets banking-grade security standards.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Professional Background</h2>
                                <p className={styles.bioText}>
                                    Outside of OTA TECH, Kankam is a Senior Software Engineer specializing in <strong>DevSecOps</strong> at <strong>Santander</strong> (Madrid/Remote). His experience spans designing secure financial payment platforms and optimizing high-traffic e-commerce systems for companies like Jumia Group and GainEx Labs.
                                </p>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Technical Arsenal</h2>
                                <div className={styles.skillsGrid} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '1rem' }}>
                                    <div>
                                        <strong><Cloud size={14} style={{display:'inline', marginRight:'6px'}}/> Cloud & Infra</strong>
                                        <p style={{fontSize:'0.9rem', color:'var(--zinc-500)'}}>AWS (Certified Solutions Architect), Docker, Kubernetes, Linux Foundation Certified.</p>
                                    </div>
                                    <div>
                                        <strong><ShieldCheck size={14} style={{display:'inline', marginRight:'6px'}}/> Security & Ops</strong>
                                        <p style={{fontSize:'0.9rem', color:'var(--zinc-500)'}}>DevSecOps pipeline integration, Automated Vulnerability Scanning, Python scripting.</p>
                                    </div>
                                </div>
                            </motion.div>

                            <motion.div className={styles.bioSection} variants={fadeInUp}>
                                <h2>Education</h2>
                                <p className={styles.bioText}>
                                    He holds a Bachelor's degree in Materials Engineering from the University of Ghana and is currently pursuing advanced certifications in Cloud Native Computing (KCNA).
                                </p>
                            </motion.div>

                        </motion.div>
                    </div>
                </div>
                <div className={styles.heroFade}></div>
            </section>
        </div>
    );
};

export default ProfileKankam;